import { useState, useEffect, useRef, useCallback } from "react";
import { X, Droplets, Plus, Minus, Timer, RotateCcw } from "lucide-react";
import { supabase } from "../supabase";
import { getDeviceId } from "../utils/device";
import type { Language } from "../data";

const LATE_THRESHOLD_MS = 2 * 60 * 60 * 1000; // 2 hours
const REMINDER_INTERVAL_MS = 15 * 60 * 1000;   // 15 minutes (glass-based banner)
const BANNER_DURATION_MS = 5 * 1000;            // 5 seconds
const SIP_ML = 25;                              // 1 sip ≈ 25 ml
const SIP_BANNER_DURATION_MS = 8 * 1000;        // sip reminder banner stays 8s

interface WaterReminderProps {
  lang: Language;
  onToast: (msg: string, type?: "success" | "error") => void;
  reminderEnabled: boolean;
  onToggleReminder: () => void;
}

const TODAY = () => new Date().toISOString().slice(0, 10);

const T = {
  ar: {
    title: "تذكير شرب الماء",
    glasses: "كوب",
    of: "من",
    target: "الهدف اليومي",
    done: "أحسنت! وصلت هدفك",
    reminder: "وقت شرب الماء! 💧",
    lateReminder: "لم تشرب ماءً منذ أكثر من ساعتين! 💧",
    addGlass: "أضف كوبًا",
    cups: ["كوب", "كوبان", "أكواب"],
    progress: (g: number, t: number) => `${g} / ${t} كوب`,
    bannerMsg: "حان وقت شرب الماء! 💧 لا تنسَ صحتك",
    // Intermittent Water Drinking
    sipsTitle: "الشرب المتقطع للماء",
    sipsSubtitle: "خذ 3–4 رشفات من الماء الآن",
    sipBannerMsg: "خذ 3–4 رشفات من الماء الآن 💧",
    sipBannerSub: (n: number, ml: number) => `${n} رشفة • ≈ ${ml} مل اليوم`,
    addSip: "رشفة (+1)",
    addSip3: "3 رشفات",
    removeSip: "رشفة (-1)",
    clearSips: "مسح الرشفات",
    totalSips: "إجمالي الرشفات اليوم",
    estimatedAmount: "الكمية المقدّرة",
    intervalLabel: "فاصل التذكير (دقيقة)",
    nextSipIn: "التذكير التالي بعد",
    minutes: "دقيقة",
    seconds: "ثانية",
    resetSips: "تصفير الرشفات",
    sipUnit: "رشفة",
    sipsUnit: "رشفات",
    ml: "مل",
    reminderToggleOn: "تذكير شرب الماء مفعّل",
    reminderToggleOff: "تذكير شرب الماء معطّل",
    reminderToggle: "تذكير شرب الماء",
  },
  en: {
    title: "Water Reminder",
    glasses: "glass",
    of: "of",
    target: "Daily target",
    done: "Goal reached!",
    reminder: "Time to drink water! 💧",
    lateReminder: "You haven't had water in over 2 hours! 💧",
    addGlass: "Add glass",
    cups: ["glass", "glasses", "glasses"],
    progress: (g: number, t: number) => `${g} / ${t} glasses`,
    bannerMsg: "Time to drink water! 💧 Stay hydrated",
    // Intermittent Water Drinking
    sipsTitle: "Intermittent Water Drinking",
    sipsSubtitle: "Take 3–4 sips of water now",
    sipBannerMsg: "Take 3–4 sips of water now 💧",
    sipBannerSub: (n: number, ml: number) => `${n} sips • ≈ ${ml} ml today`,
    addSip: "Sip (+1)",
    addSip3: "3 sips",
    removeSip: "Sip (-1)",
    clearSips: "Clear sips",
    totalSips: "Total sips today",
    estimatedAmount: "Estimated amount",
    intervalLabel: "Reminder interval (min)",
    nextSipIn: "Next reminder in",
    minutes: "min",
    seconds: "sec",
    resetSips: "Reset sips",
    sipUnit: "sip",
    sipsUnit: "sips",
    ml: "ml",
    reminderToggleOn: "Water reminder enabled",
    reminderToggleOff: "Water reminder disabled",
    reminderToggle: "Water reminder",
  },
};

export function WaterReminder({ lang, onToast, reminderEnabled, onToggleReminder }: WaterReminderProps) {
  const [open, setOpen] = useState(false);
  const [glasses, setGlasses] = useState(0);
  const [target, setTarget] = useState(8);
  const [loaded, setLoaded] = useState(false);
  const [isLate, setIsLate] = useState(false);
  const [showBanner, setShowBanner] = useState(false);
  // Intermittent Water Drinking state
  const [sips, setSips] = useState(0);
  const [lastSipAt, setLastSipAt] = useState<number | null>(null);
  const [sipIntervalMin, setSipIntervalMin] = useState(25);
  const [showSipBanner, setShowSipBanner] = useState(false);
  const [countdown, setCountdown] = useState(0);
  const [tab, setTab] = useState<"glasses" | "sips">("glasses");
  const panelRef = useRef<HTMLDivElement>(null);
  const bannerTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const sipBannerTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const sipIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const countdownRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const deviceId = getDeviceId();
  const t = T[lang];

  const lateKey = `water_last_drink_${deviceId}_${new Date().toISOString().slice(0, 10)}`;

  const getLastDrinkTime = (): number => {
    const stored = localStorage.getItem(lateKey);
    if (stored) return parseInt(stored, 10);
    const sod = new Date(); sod.setHours(0, 0, 0, 0);
    return sod.getTime();
  };

  const recordDrink = () => {
    localStorage.setItem(lateKey, Date.now().toString());
    setIsLate(false);
  };

  const checkLate = useCallback((currentGlasses: number, currentTarget: number) => {
    if (currentGlasses >= currentTarget) { setIsLate(false); return; }
    const elapsed = Date.now() - getLastDrinkTime();
    setIsLate(elapsed >= LATE_THRESHOLD_MS);
  }, [lateKey]); // eslint-disable-line react-hooks/exhaustive-deps

  const triggerBanner = useCallback((currentGlasses: number, currentTarget: number) => {
    if (!reminderEnabled) return;
    if (currentGlasses >= currentTarget) return;
    setShowBanner(true);
    if (bannerTimerRef.current) clearTimeout(bannerTimerRef.current);
    bannerTimerRef.current = setTimeout(() => {
      setShowBanner(false);
    }, BANNER_DURATION_MS);
  }, []);

  const loadToday = useCallback(async () => {
    const { data } = await supabase
      .from("water_intake")
      .select("glasses, target, sips, last_sip_at, sip_interval_min")
      .eq("device_id", deviceId)
      .eq("date", TODAY())
      .maybeSingle();
    const g = data?.glasses ?? 0;
    const tgt = data?.target ?? 8;
    const s = data?.sips ?? 0;
    const lsa = data?.last_sip_at ? new Date(data.last_sip_at).getTime() : null;
    const interval = data?.sip_interval_min ?? 25;
    if (data) { setGlasses(g); setTarget(tgt); }
    setSips(s);
    setLastSipAt(lsa);
    setSipIntervalMin(interval);
    setLoaded(true);
    checkLate(g, tgt);
  }, [deviceId, checkLate]);

  useEffect(() => { loadToday(); }, [loadToday]);

  const save = useCallback(async (newGlasses: number, newTarget: number, extra?: Partial<{ sips: number; last_sip_at: string | null; sip_interval_min: number }>) => {
    await supabase
      .from("water_intake")
      .upsert(
        {
          device_id: deviceId,
          date: TODAY(),
          glasses: newGlasses,
          target: newTarget,
          updated_at: new Date().toISOString(),
          ...extra,
        },
        { onConflict: "device_id,date" }
      );
  }, [deviceId]);

  const addGlass = useCallback(async () => {
    const next = Math.min(glasses + 1, 20);
    setGlasses(next);
    recordDrink();
    setShowBanner(false);
    if (bannerTimerRef.current) clearTimeout(bannerTimerRef.current);
    await save(next, target);
    if (next === target) onToast(lang === "ar" ? "أحسنت! وصلت هدفك اليومي 🎉" : "Goal reached! 🎉");
  }, [glasses, target, save, onToast, lang]); // eslint-disable-line react-hooks/exhaustive-deps

  const removeGlass = useCallback(async () => {
    const next = Math.max(glasses - 1, 0);
    setGlasses(next);
    await save(next, target);
  }, [glasses, target, save]);

  const changeTarget = useCallback(async (val: number) => {
    setTarget(val);
    await save(glasses, val);
  }, [glasses, save]);

  // ── Intermittent Water Drinking: sip actions ──
  const addSips = useCallback(async (count: number) => {
    const next = sips + count;
    const now = Date.now();
    setSips(next);
    setLastSipAt(now);
    setShowSipBanner(false);
    if (sipBannerTimerRef.current) clearTimeout(sipBannerTimerRef.current);
    await save(glasses, target, {
      sips: next,
      last_sip_at: new Date(now).toISOString(),
    });
    onToast(lang === "ar" ? `+${count} رشفة 💧` : `+${count} sip 💧`, "success");
  }, [sips, glasses, target, save, onToast, lang]); // eslint-disable-line react-hooks/exhaustive-deps

  const removeSip = useCallback(async () => {
    const next = Math.max(sips - 1, 0);
    setSips(next);
    if (next === 0) setLastSipAt(null);
    await save(glasses, target, {
      sips: next,
      last_sip_at: next === 0 ? null : lastSipAt ? new Date(lastSipAt).toISOString() : undefined,
    });
    onToast(lang === "ar" ? "-1 رشفة" : "-1 sip", "success");
  }, [sips, glasses, target, lastSipAt, save, onToast, lang]); // eslint-disable-line react-hooks/exhaustive-deps

  const clearSips = useCallback(async () => {
    setSips(0);
    setLastSipAt(null);
    await save(glasses, target, { sips: 0, last_sip_at: null });
    onToast(lang === "ar" ? "تم مسح الرشفات" : "Sips cleared", "success");
  }, [glasses, target, save, onToast, lang]);

  const resetSips = useCallback(async () => {
    setSips(0);
    setLastSipAt(null);
    await save(glasses, target, { sips: 0, last_sip_at: null });
    onToast(lang === "ar" ? "تم تصفير الرشفات" : "Sips reset", "success");
  }, [glasses, target, save, onToast, lang]);

  const changeSipInterval = useCallback(async (val: number) => {
    const clamped = Math.min(Math.max(val, 5), 120);
    setSipIntervalMin(clamped);
    await save(glasses, target, { sip_interval_min: clamped });
  }, [glasses, target, save]);

  // Repeating 15-minute glass reminder
  useEffect(() => {
    if (!loaded) return;
    if (intervalRef.current) clearInterval(intervalRef.current);
    intervalRef.current = setInterval(() => {
      if (glasses < target) {
        triggerBanner(glasses, target);
        checkLate(glasses, target);
      }
    }, REMINDER_INTERVAL_MS);
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
      if (bannerTimerRef.current) clearTimeout(bannerTimerRef.current);
    };
  }, [loaded, glasses, target, triggerBanner, checkLate]);

  // Intermittent sip reminder — every sipIntervalMin minutes
  const triggerSipBanner = useCallback(() => {
    if (!reminderEnabled) return;
    setShowSipBanner(true);
    if (sipBannerTimerRef.current) clearTimeout(sipBannerTimerRef.current);
    sipBannerTimerRef.current = setTimeout(() => {
      setShowSipBanner(false);
    }, SIP_BANNER_DURATION_MS);
  }, []);

  useEffect(() => {
    if (!loaded) return;
    const intervalMs = sipIntervalMin * 60 * 1000;
    // Compute time until next sip reminder
    const computeNext = () => {
      const base = lastSipAt ?? Date.now();
      const elapsed = Date.now() - base;
      const remaining = Math.max(0, intervalMs - elapsed);
      setCountdown(Math.ceil(remaining / 1000));
      return remaining;
    };
    const nextIn = computeNext();

    // Schedule the first reminder
    const firstTimer = setTimeout(() => {
      triggerSipBanner();
      // Then repeat
      if (sipIntervalRef.current) clearInterval(sipIntervalRef.current);
      sipIntervalRef.current = setInterval(() => {
        triggerSipBanner();
      }, intervalMs);
    }, nextIn);

    // Countdown ticker (updates every second)
    if (countdownRef.current) clearInterval(countdownRef.current);
    countdownRef.current = setInterval(() => {
      setCountdown(prev => (prev > 0 ? prev - 1 : 0));
    }, 1000);

    return () => {
      clearTimeout(firstTimer);
      if (sipIntervalRef.current) clearInterval(sipIntervalRef.current);
      if (countdownRef.current) clearInterval(countdownRef.current);
      if (sipBannerTimerRef.current) clearTimeout(sipBannerTimerRef.current);
    };
  }, [loaded, lastSipAt, sipIntervalMin, triggerSipBanner]);

  // Check late status every minute
  useEffect(() => {
    if (!loaded) return;
    const interval = setInterval(() => {
      checkLate(glasses, target);
    }, 60 * 1000);
    checkLate(glasses, target);
    return () => clearInterval(interval);
  }, [loaded, glasses, target, checkLate]);

  // Close panel on outside click
  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (panelRef.current && !panelRef.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open]);

  const pct = target > 0 ? Math.min((glasses / target) * 100, 100) : 0;
  const reached = glasses >= target;
  const sipMl = sips * SIP_ML;
  const fmtCountdown = (secs: number) => {
    const m = Math.floor(secs / 60);
    const s = secs % 60;
    if (lang === "ar") return `${m} ${t.minutes} ${s} ${t.seconds}`;
    return `${m}:${s.toString().padStart(2, "0")} ${t.minutes}`;
  };

  return (
    <>
      {/* ── Floating 5-second glass reminder banner ── */}
      {showBanner && !reached && (
        <div
          className="fixed top-4 left-1/2 z-[9999] pointer-events-auto"
          style={{
            transform: "translateX(-50%)",
            animation: "water-banner-in 0.4s cubic-bezier(.4,0,.2,1) forwards",
          }}
        >
          <style>{`
            @keyframes water-banner-in {
              from { opacity: 0; transform: translateX(-50%) translateY(-16px) scale(0.95); }
              to   { opacity: 1; transform: translateX(-50%) translateY(0)     scale(1); }
            }
            @keyframes water-sip-banner-in {
              from { opacity: 0; transform: translateX(-50%) translateY(-20px) scale(0.92); }
              to   { opacity: 1; transform: translateX(-50%) translateY(0)     scale(1); }
            }
          `}</style>
          <div
            className="flex items-center gap-3 px-5 py-3 rounded-2xl shadow-2xl border border-cyan-400/30 text-white font-bold text-sm"
            style={{
              background: "linear-gradient(135deg, #0e7490 0%, #0891b2 60%, #22d3ee 100%)",
              boxShadow: "0 8px 32px -8px rgba(6,182,212,0.6), 0 2px 8px rgba(0,0,0,0.3)",
              minWidth: 260,
            }}
          >
            <div className="w-9 h-9 rounded-full bg-white/20 flex items-center justify-center shrink-0 animate-bounce">
              <Droplets className="w-5 h-5 text-white" />
            </div>
            <div className="flex-1">
              <p className="text-white font-black text-sm leading-tight">{t.bannerMsg}</p>
              <p className="text-cyan-100/70 text-[11px] mt-0.5">
                {glasses}/{target} {lang === "ar" ? "كوب" : "glasses"}
              </p>
            </div>
            <button
              onClick={() => setShowBanner(false)}
              className="w-6 h-6 rounded-full bg-white/20 hover:bg-white/30 flex items-center justify-center shrink-0 transition-colors"
            >
              <X className="w-3 h-3" />
            </button>
          </div>
        </div>
      )}

      {/* ── Floating intermittent sip reminder banner ── */}
      {showSipBanner && (
        <div
          className="fixed top-4 left-1/2 z-[10000] pointer-events-auto"
          style={{
            transform: "translateX(-50%)",
            animation: "water-sip-banner-in 0.4s cubic-bezier(.4,0,.2,1) forwards",
          }}
        >
          <div
            className="flex items-center gap-3 px-5 py-3 rounded-2xl shadow-2xl border border-sky-300/40 text-white font-bold text-sm"
            style={{
              background: "linear-gradient(135deg, #0369a1 0%, #0284c7 55%, #38bdf8 100%)",
              boxShadow: "0 10px 36px -8px rgba(2,132,199,0.7), 0 2px 8px rgba(0,0,0,0.3)",
              minWidth: 280,
            }}
          >
            <div className="w-9 h-9 rounded-full bg-white/20 flex items-center justify-center shrink-0" style={{ animation: "water-banner-in 0.4s cubic-bezier(.4,0,.2,1) forwards" }}>
              <Droplets className="w-5 h-5 text-white animate-pulse" />
            </div>
            <div className="flex-1">
              <p className="text-white font-black text-sm leading-tight">{t.sipBannerMsg}</p>
              <p className="text-sky-100/80 text-[11px] mt-0.5">{t.sipBannerSub(sips, sipMl)}</p>
            </div>
            <button
              onClick={() => addSips(3)}
              className="shrink-0 px-3 py-1.5 rounded-xl bg-white/25 hover:bg-white/35 text-white text-xs font-bold transition-colors"
            >
              {lang === "ar" ? "شربتُ 3" : "Drank 3"}
            </button>
            <button
              onClick={() => setShowSipBanner(false)}
              className="w-6 h-6 rounded-full bg-white/20 hover:bg-white/30 flex items-center justify-center shrink-0 transition-colors"
            >
              <X className="w-3 h-3" />
            </button>
          </div>
        </div>
      )}

      <div className="relative" ref={panelRef}>
        {/* Header button */}
        <button
          onClick={() => setOpen(o => !o)}
          title={t.title}
          className="flex items-center gap-2.5 px-3 h-11 rounded-xl hover:bg-stone-100 dark:hover:bg-slate-700 transition-colors min-w-0"
        >
          <div className="relative shrink-0">
            <Droplets className={`w-5 h-5 ${
              reached ? "text-emerald-500" : isLate ? "text-red-500 animate-pulse" : "text-blue-600"
            }`} />
            {sips > 0 && (
              <span className="absolute -top-1 -end-1 min-w-[16px] h-4 px-1 rounded-full bg-sky-500 text-white text-[9px] font-bold flex items-center justify-center leading-none">
                {sips}
              </span>
            )}
          </div>
          <span className={`text-sm font-medium tabular-nums truncate ${
            reached ? "text-emerald-600 dark:text-emerald-400" : isLate ? "text-red-600 dark:text-red-400" : "text-stone-700 dark:text-slate-200"
          }`}>{lang === "ar" ? `الماء: ${glasses}/${target} أكواب` : `Water: ${glasses}/${target} cups`}</span>
        </button>

        {/* Modal overlay — centered on screen */}
        {open && (
          <div
            className="fixed inset-0 z-[9998] flex items-center justify-center bg-black/50 backdrop-blur-sm p-4"
            onClick={() => setOpen(false)}
            style={{ animation: "water-fade-in 0.2s ease-out forwards" }}
          >
            <style>{`
              @keyframes water-fade-in { from { opacity: 0; } to { opacity: 1; } }
              @keyframes water-modal-in {
                from { opacity: 0; transform: translateY(12px) scale(0.96); }
                to   { opacity: 1; transform: translateY(0)    scale(1); }
              }
            `}</style>
            <div
              className="w-80 bg-[#1A2A2E] dark:bg-[#0D1E22] border border-cyan-900/50 rounded-2xl shadow-2xl overflow-hidden"
              dir={lang === "ar" ? "rtl" : "ltr"}
              onClick={e => e.stopPropagation()}
              style={{ animation: "water-modal-in 0.25s cubic-bezier(.4,0,.2,1) forwards" }}
            >
            {/* Header */}
            <div className="flex items-center justify-between px-4 py-3 bg-gradient-to-r from-cyan-900/60 to-blue-900/60 border-b border-cyan-800/40">
              <div className="flex items-center gap-2">
                <Droplets className="w-4 h-4 text-cyan-400" />
                <span className="text-sm font-bold text-cyan-100">{t.title}</span>
              </div>
              <button onClick={() => setOpen(false)} className="w-6 h-6 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center text-white/60 hover:text-white transition-colors">
                <X className="w-3 h-3" />
              </button>
            </div>

            {/* Reminder toggle */}
            <div className="flex items-center justify-between px-4 py-3 mx-2 mt-2 mb-1 rounded-xl bg-white/5 border border-white/10">
              <div className="flex items-center gap-2">
                <Droplets className={`w-4 h-4 ${reminderEnabled ? "text-cyan-400" : "text-white/30"}`} />
                <span className={`text-xs font-bold ${reminderEnabled ? "text-cyan-100" : "text-white/50"}`}>{t.reminderToggle}</span>
              </div>
              <button
                onClick={onToggleReminder}
                className="relative w-11 h-6 rounded-full transition-colors duration-200"
                style={{
                  background: reminderEnabled
                    ? "linear-gradient(to right, #0891b2, #22d3ee)"
                    : "rgba(148,163,184,0.25)",
                  boxShadow: reminderEnabled ? "0 0 8px rgba(34,211,238,0.4)" : "inset 0 1px 3px rgba(0,0,0,0.3)",
                }}
                aria-label={t.reminderToggle}
              >
                <span
                  className="absolute top-0.5 w-5 h-5 rounded-full bg-white shadow-md transition-all duration-200"
                  style={{
                    left: reminderEnabled ? "22px" : "2px",
                  }}
                />
              </button>
            </div>

            {/* Tab switcher */}
            <div className="flex gap-1 p-2 pb-0">
              <button
                onClick={() => setTab("glasses")}
                className={`flex-1 flex items-center justify-center gap-1.5 py-2 rounded-xl text-xs font-bold transition-all ${
                  tab === "glasses"
                    ? "bg-cyan-500 text-white shadow"
                    : "bg-white/5 text-white/50 hover:bg-white/10 hover:text-white/80"
                }`}
              >
                <Droplets className="w-3.5 h-3.5" />
                {t.title}
              </button>
              <button
                onClick={() => setTab("sips")}
                className={`flex-1 flex items-center justify-center gap-1.5 py-2 rounded-xl text-xs font-bold transition-all ${
                  tab === "sips"
                    ? "bg-sky-500 text-white shadow"
                    : "bg-white/5 text-white/50 hover:bg-white/10 hover:text-white/80"
                }`}
              >
                <Timer className="w-3.5 h-3.5" />
                {t.sipsTitle}
              </button>
            </div>

            <div className="p-4 space-y-4">
              {/* ── Glasses tab ── */}
              {tab === "glasses" && (
                <>
                  {/* Late warning */}
                  {isLate && !reached && (
                    <div className="flex items-center gap-2 px-3 py-2 rounded-xl bg-red-500/15 border border-red-500/30 text-red-300 text-xs font-medium">
                      <span className="text-base leading-none">⏰</span>
                      <span>{t.lateReminder}</span>
                    </div>
                  )}

                  {/* Progress count */}
                  <div className="text-center">
                    <div className="text-4xl font-black text-white tabular-nums leading-none">{glasses}</div>
                    <div className="text-xs text-cyan-300/70 mt-1">{t.progress(glasses, target)}</div>
                  </div>

                  {/* Visual drops */}
                  <div className="flex flex-wrap gap-1.5 justify-center">
                    {Array.from({ length: target }).map((_, i) => (
                      <button
                        key={i}
                        onClick={() => {
                          const next = i < glasses ? i : i + 1;
                          setGlasses(next);
                          save(next, target);
                        }}
                        className="transition-all duration-200"
                        title={`${i + 1}`}
                      >
                        <Droplets
                          className={`w-5 h-5 transition-all ${
                            i < glasses
                              ? "text-cyan-400 drop-shadow-[0_0_4px_rgba(34,211,238,0.6)]"
                              : "text-white/15"
                          }`}
                        />
                      </button>
                    ))}
                  </div>

                  {/* Progress bar */}
                  <div className="h-2 bg-white/10 rounded-full overflow-hidden">
                    <div
                      className={`h-full rounded-full transition-all duration-500 ${reached ? "bg-emerald-400" : "bg-cyan-400"}`}
                      style={{ width: `${pct}%` }}
                    />
                  </div>

                  {reached && (
                    <div className="text-center text-xs font-bold text-emerald-400 py-1">
                      🎉 {t.done}
                    </div>
                  )}

                  {/* +/- controls */}
                  <div className="flex items-center gap-3">
                    <button
                      onClick={removeGlass}
                      disabled={glasses === 0}
                      className="w-10 h-10 rounded-full bg-white/10 hover:bg-white/20 disabled:opacity-30 disabled:cursor-not-allowed flex items-center justify-center text-white transition-colors"
                    >
                      <Minus className="w-4 h-4" />
                    </button>
                    <button
                      onClick={addGlass}
                      className="flex-1 flex items-center justify-center gap-2 h-10 rounded-xl bg-cyan-500 hover:bg-cyan-400 text-white font-bold text-sm transition-colors shadow"
                    >
                      <Plus className="w-4 h-4" />
                      {t.addGlass}
                    </button>
                    <button
                      disabled
                      aria-hidden
                      className="w-10 h-10 rounded-full bg-white/10 opacity-0 pointer-events-none"
                    >
                      <Minus className="w-4 h-4" />
                    </button>
                  </div>

                  {/* Target selector */}
                  <div>
                    <p className="text-[11px] text-white/40 mb-2 text-center">{t.target}</p>
                    <div className="flex gap-1.5 justify-center flex-wrap">
                      {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12].map(n => (
                        <button
                          key={n}
                          onClick={() => changeTarget(n)}
                          className={`w-9 h-9 rounded-xl text-xs font-bold border transition-all ${
                            target === n
                              ? "bg-cyan-500 border-cyan-500 text-white shadow"
                              : "border-white/15 text-white/50 hover:border-cyan-500/50 hover:text-white/80"
                          }`}
                        >
                          {n}
                        </button>
                      ))}
                    </div>
                  </div>
                </>
              )}

              {/* ── Intermittent Sips tab ── */}
              {tab === "sips" && (
                <>
                  {/* Subtitle / instruction */}
                  <div className="flex items-center gap-2 px-3 py-2 rounded-xl bg-sky-500/15 border border-sky-500/30 text-sky-200 text-xs font-medium">
                    <Droplets className="w-3.5 h-3.5 shrink-0" />
                    <span>{t.sipsSubtitle}</span>
                  </div>

                  {/* Countdown to next reminder */}
                  <div className="text-center">
                    <div className="flex items-center justify-center gap-1.5 text-sky-300/80 text-[11px] font-semibold mb-1">
                      <Timer className="w-3.5 h-3.5" />
                      {t.nextSipIn}
                    </div>
                    <div className="text-2xl font-black text-white tabular-nums leading-none">
                      {fmtCountdown(countdown)}
                    </div>
                  </div>

                  {/* Total sips + estimated amount */}
                  <div className="grid grid-cols-2 gap-2">
                    <div className="rounded-xl bg-white/5 border border-white/10 px-3 py-3 text-center">
                      <div className="text-3xl font-black text-sky-300 tabular-nums leading-none">{sips}</div>
                      <div className="text-[10px] text-white/40 mt-1.5 font-semibold">{t.totalSips}</div>
                    </div>
                    <div className="rounded-xl bg-white/5 border border-white/10 px-3 py-3 text-center">
                      <div className="text-3xl font-black text-cyan-300 tabular-nums leading-none">{sipMl}</div>
                      <div className="text-[10px] text-white/40 mt-1.5 font-semibold">{t.estimatedAmount} ({t.ml})</div>
                    </div>
                  </div>

                  {/* Conversion note */}
                  <p className="text-center text-[10px] text-white/35 leading-snug">
                    {lang === "ar"
                      ? `رشفة واحدة ≈ ${SIP_ML} مل • الكمية اليومية المقدّرة = ${sips} × ${SIP_ML} = ${sipMl} مل`
                      : `1 sip ≈ ${SIP_ML} ml • Estimated daily amount = ${sips} × ${SIP_ML} = ${sipMl} ml`}
                  </p>

                  {/* Sip action buttons */}
                  <div className="flex items-center gap-2">
                    <button
                      onClick={removeSip}
                      disabled={sips === 0}
                      className="w-10 h-10 rounded-xl bg-white/10 hover:bg-white/20 disabled:opacity-30 disabled:cursor-not-allowed flex items-center justify-center text-white transition-colors shrink-0"
                      title={t.removeSip}
                    >
                      <Minus className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => addSips(1)}
                      className="flex-1 flex items-center justify-center gap-1.5 h-10 rounded-xl bg-sky-500 hover:bg-sky-400 text-white font-bold text-sm transition-colors shadow"
                    >
                      <Plus className="w-4 h-4" />
                      {t.addSip}
                    </button>
                    <button
                      onClick={() => addSips(3)}
                      className="flex-1 flex items-center justify-center gap-1.5 h-10 rounded-xl bg-cyan-500 hover:bg-cyan-400 text-white font-bold text-sm transition-colors shadow"
                    >
                      <Droplets className="w-4 h-4" />
                      {t.addSip3}
                    </button>
                  </div>

                  {/* Clear sips (suspicion/mistake) */}
                  <button
                    onClick={clearSips}
                    disabled={sips === 0}
                    className="w-full flex items-center justify-center gap-1.5 h-9 rounded-xl bg-red-500/10 hover:bg-red-500/20 disabled:opacity-30 disabled:cursor-not-allowed text-red-400 hover:text-red-300 text-xs font-semibold transition-colors border border-red-500/20"
                  >
                    <X className="w-3.5 h-3.5" />
                    {t.clearSips}
                  </button>

                  {/* Interval selector */}
                  <div>
                    <p className="text-[11px] text-white/40 mb-2 text-center">{t.intervalLabel}</p>
                    <div className="flex gap-1.5 justify-center flex-wrap">
                      {[15, 20, 25, 30, 45, 60, 90].map(n => (
                        <button
                          key={n}
                          onClick={() => changeSipInterval(n)}
                          className={`px-3 h-9 rounded-xl text-xs font-bold border transition-all ${
                            sipIntervalMin === n
                              ? "bg-sky-500 border-sky-500 text-white shadow"
                              : "border-white/15 text-white/50 hover:border-sky-500/50 hover:text-white/80"
                          }`}
                        >
                          {n}
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Reset */}
                  <button
                    onClick={resetSips}
                    disabled={sips === 0}
                    className="w-full flex items-center justify-center gap-1.5 h-9 rounded-xl bg-white/5 hover:bg-white/10 disabled:opacity-30 disabled:cursor-not-allowed text-white/60 hover:text-white text-xs font-semibold transition-colors"
                  >
                    <RotateCcw className="w-3.5 h-3.5" />
                    {t.resetSips}
                  </button>
                </>
              )}
            </div>
            </div>
          </div>
        )}
      </div>
    </>
  );
}
